import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, finalize, switchMap, take, throwError } from 'rxjs';
import { TokenResponse } from '../models/token-response';
import { StorageService } from './storage.service';
import { SessionService } from './session.service';
import { AuthenticationService } from './authentication.service';

interface JwtPayload {
  exp: number;
}

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  if (request.url.includes('/api/connect/refresh')) {
    return next(request);
  }
  const storageService = inject(StorageService);
  const authenticationService = inject(AuthenticationService);
  const token = storageService.token;
  if (token) {
    if (authenticationService.isTokenExpired()) {
      // Token is expired, refresh it
      return authenticationService.refreshAuthenticationToken().pipe(
        switchMap((newToken: TokenResponse) => {
          if (newToken.access_token) {
            addToken(request, newToken);
            const clonedRequest = request.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken.access_token}`,
              },
            });
            return next(clonedRequest);
          }
          // If we don't get a new token, we are in trouble so logout.
          return logoutUser();
        }),
        catchError(() => {
          // If there is an exception calling 'refreshToken', bad news so logout.
          return logoutUser();
        })
      );
    }
    else {
      request = request.clone({ headers: request.headers.set("Authorization", `Bearer ${token}`) });
    }
  }

  return next(request).pipe(
    catchError((err: HttpErrorResponse) => {
      switch ((err).status) {
        case 400:
          return handle400Error(err);
        case 401:
          return handle401Error(request, next, err, authenticationService);
        case 403:
          return handle403Error();
        default:
          return throwError(() => { return err; });
      }
    })
  );
};

export function handle400Error(error: HttpErrorResponse) {
  if (error && error.status === 400 && error.error && error.error.error === "invalid_grant") {
    // If we get a 400 and the error message is 'invalid_grant', the token is no longer valid so logout.
    return logoutUser();
  }

  return throwError(() => { return error });
}

export function handle403Error() {
  return logoutUser();
}

export function handle401Error(req: HttpRequest<any>, next: HttpHandlerFn, err: HttpErrorResponse, authenticationService: AuthenticationService) {
  // const authenticationService = inject(AuthenticationService);
  let isRefreshingToken = false;
  let tokenSubject = new BehaviorSubject<TokenResponse | null>(null);
  if (err.error?.errors && err.error.errors.length > 0) {
    let error = err.error.errors[0];
    if (error.code === 0) {
      return throwError(() => { return error.message });
    }
  }
  if (!isRefreshingToken) {
    isRefreshingToken = true;

    // Reset here so that the following requests wait until the token
    // comes back from the refreshToken call.
    tokenSubject.next(null);

    return authenticationService.refreshAuthenticationToken().pipe(
      switchMap((newToken: TokenResponse) => {
        if (newToken.access_token) {
          tokenSubject.next(newToken);
          return next(addToken(req, newToken));
        }
        // If we don't get a new token, we are in trouble so logout.
        return logoutUser();
      }),
      catchError(() => {
        // If there is an exception calling 'refreshToken', bad news so logout.
        return logoutUser();
      }),
      finalize(() => {
        isRefreshingToken = false;
      }));
  } else {
    return tokenSubject.pipe(
      filter(token => token != null),
      take(1),
      switchMap((token: TokenResponse | null) => {
        return next(addToken(req, token));
      }));
  }
}

export function logoutUser() {
  const sessionService = inject(SessionService);
  sessionService.logoutAndRedirect(false);
  return throwError(() => { return "" });
}

export function addToken(request: HttpRequest<any>, token: TokenResponse | null): HttpRequest<any> {
  const sessionService = inject(SessionService);
  if (token?.access_token && token?.refresh_token)
    sessionService.saveTokens(token.access_token, token.refresh_token);
  return request;
}
